import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircleMarker, MapContainer, Marker, Popup, TileLayer, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./heatmap-leaflet.css";
import { toast } from "sonner";
import { Loader2, Sparkles, Bell, Globe } from "lucide-react";
import DarkSelect from "../components/home/DarkSelect";
import { useTranslation } from "react-i18next";
import {
  bboxFromLeafletBounds,
  fetchHeatmapBuckets,
  fetchExploreSearch,
} from "../api/heatmapApi";
import { apiClient } from "../api/client";
import { fetchSearchContext, putSearchContext } from "../api/searchContextApi";
import { ExploreListPanel } from "../components/explore/ExploreListPanel";
import { HeatmapChipBar, csvList } from "../components/heatmap/HeatmapChipBar";
import HeatmapMobileControls from "../components/heatmap/HeatmapMobileControls";
import HeatmapMobileFilterSheet from "../components/heatmap/HeatmapMobileFilterSheet";
import { SaveAlertDialog } from "../components/alerts/SaveAlertDialog";
import {
  colourFor,
  formatValue,
  getLayerById,
} from "../config/heatmap-layers";
import { isMapLibreEnabled } from "../config/mapProvider";
import HeatmapMapLibre from "../components/heatmap/HeatmapMapLibre";
import SpotlightTour from "../tour/SpotlightTour";
import { useTour } from "../tour/useTour";

// Leaflet's default-marker fix isn't needed (we use CircleMarker), but
// silence the dev warning that leaks otherwise.
delete L.Icon.Default.prototype._getIconUrl;

// Welle 4.1 — one borderless European map. Pan & zoom decide; the
// backend works purely on the viewport bounding box (no country logic).
const EUROPE_VIEW = { center: [48.3, 12.5], zoom: 5 };

const PERFORMANCE_BUCKET_CAP = 500;
const MOVE_DEBOUNCE_MS = 350;
const MIN_SAMPLES_DEFAULT = 5;
const BUCKET_TYPES = ["auto", "postal_code", "district", "geo_grid_500m"];

// Welle 4.2 — deal markers: pill with the live market-%.
// Welle 4.3 — colour follows market_position; low-confidence markers
// render dashed/dimmed so weak data is never presented as certainty.
const POSITION_PILL = {
  strong_below_market: "below",
  below_market: "below",
  near_market: "near",
  above_market: "above",
  strong_above_market: "strong-above",
  unknown: "near",
};

function dealIcon(d) {
  const pct = d?.price_vs_market_pct;
  const style = POSITION_PILL[d?.market_position] || "near";
  let label = "Deal";
  let arrow = "▲";
  if (typeof pct === "number") {
    const n = Math.abs(Math.round(pct));
    if (pct > 0) {
      label = `+${n} %`;
      arrow = "▼";
    } else {
      label = `−${n} %`;
    }
  }
  const lowConf = d?.low_confidence ? " deal-pill--lowconf" : "";
  return L.divIcon({
    className: "heatmap-deal-marker",
    html: `<span class="deal-pill deal-pill--${style}${lowConf}">${arrow} ${label}</span>`,
    iconSize: [0, 0],
  });
}

// Welle 4.3 — market-position filter (Investment-Explorer-Basis) is now
// driven by the SHARED deal-map contract (☑ Gute ☑ Normale ☑ Über Markt).
const DEALS_CACHE_TTL_MS = 60000;

// V4.12 Phase C — context-dependent chip UX. `v` (vertical) + `mode`
// drive what the map shows; all other keys are the CANONICAL explore
// params (identical vocabulary to the AI search context).
const DEFAULT_FILTERS = {
  v: "immo",
  mode: "deals",
  tt: "buy",
  price_min: "",
  price_max: "",
  area_min: "",
  area_max: "",
  rooms_min: "",
  yield_min: "",
  discount_min: "",
  types: "",
  region: "",
  country: "",
  features: "",
  makes: "",
  models: "",
  year_min: "",
  year_max: "",
  km_max: "",
  fuels: "",
  gearboxes: "",
  body_types: "",
  seller_types: "",
  damage: "",
  price_reduced: "",
  status: "",
};
const FILTER_URL_KEYS = Object.keys(DEFAULT_FILTERS);
// Master-Sprint 2026-06 — "alle"/"ueberteuert" are legacy modes: "Markt"
// now means the FULL market and carries a market-status sub-filter
// (filters.status). Legacy URLs/alerts are normalized on init.
const VALID_MODES = {
  auto: ["deals", "markt"],
  immo: ["deals", "markt", "preis_m2", "rendite"],
};
const LEGACY_MODE_MAP = {
  alle: { mode: "markt", status: "" },
  ueberteuert: { mode: "markt", status: "above_market_all" },
};
// Detail filters (everything that lives in the mobile bottom sheet).
const DETAIL_FILTER_KEYS = FILTER_URL_KEYS.filter(
  (k) => !["v", "mode", "tt", "status"].includes(k),
);

// Shared search context (AI ↔ Heatmap) — canonical filters → chip state.
function contextToFilterPatch(context) {
  const cf = context?.filters || {};
  if (!Object.keys(cf).length) return null;
  const v = context.vertical === "auto" ? "auto" : "immo";
  const patch = { ...DEFAULT_FILTERS, v, mode: v === "auto" ? "alle" : "markt" };
  const csvMap = {
    makes: "makes",
    models: "models",
    fuels: "fuels",
    gearboxes: "gearboxes",
    body_types: "body_types",
    features: "features",
    seller_types: "seller_types",
    object_types: "types",
  };
  Object.entries(csvMap).forEach(([src, dst]) => {
    if (Array.isArray(cf[src]) && cf[src].length) patch[dst] = cf[src].join(",");
  });
  [
    "year_min", "year_max", "km_max", "price_min", "price_max",
    "rooms_min", "area_min", "area_max", "yield_min",
  ].forEach((k) => {
    if (cf[k] !== null && cf[k] !== undefined && cf[k] !== "") patch[k] = String(cf[k]);
  });
  if (cf.transaction_type === "rent" || cf.transaction_type === "buy") {
    patch.tt = cf.transaction_type;
  }
  return patch;
}

function filtersToContextPayload(filters, vertical) {
  const out = {};
  const csvMap =
    vertical === "auto"
      ? { makes: "makes", models: "models", fuels: "fuels", gearboxes: "gearboxes", body_types: "body_types", features: "features", seller_types: "seller_types" }
      : { types: "object_types", features: "features", seller_types: "seller_types" };
  Object.entries(csvMap).forEach(([src, dst]) => {
    const vals = csvList(filters[src]);
    if (vals.length) out[dst] = vals;
  });
  const numKeys =
    vertical === "auto"
      ? ["year_min", "year_max", "km_max", "price_min", "price_max"]
      : ["price_min", "price_max", "rooms_min", "area_min", "area_max", "yield_min"];
  numKeys.forEach((k) => {
    if (filters[k]) out[k] = Number(filters[k]);
  });
  if (vertical === "immo") out.transaction_type = filters.tt;
  // V4.23 Phase 3a — place + country scope round-trip into the alert so
  // "Jetzt prüfen"/scheduled runs reproduce exactly the map's result set.
  if (filters.region) out.region = filters.region;
  if (filters.country) out.country = filters.country;
  return out;
}

// Welle 5 — zoom-dependent server clusters rendered as count bubbles.
function clusterIcon(count) {
  const size = count >= 500 ? 48 : count >= 100 ? 42 : 36;
  const label = count >= 1000 ? `${Math.round(count / 100) / 10}k` : count;
  return L.divIcon({
    className: "heatmap-cluster-marker",
    html: `<span class="cluster-bubble" style="width:${size}px;height:${size}px;line-height:${size}px">${label}</span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function BboxListener({ onChange }) {
  const map = useMapEvents({
    moveend: (e) => onChange(e.target.getBounds()),
    zoomend: (e) => onChange(e.target.getBounds()),
  });
  // Welle 4.2 — fire once on mount so viewport-bound layers (deal
  // markers) load on first paint, not only after the first pan.
  useEffect(() => {
    onChange(map.getBounds());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

// Master-Sprint P0 — desktop/mobile split for the heatmap controls.
// The desktop chip wall is UNMOUNTED (not just CSS-hidden) on mobile so
// data-testids stay unique in the DOM (duplicate ids broke UI tests).
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia("(min-width: 768px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
}

export default function HeatmapPage() {
  const { t } = useTranslation("heatmap");
  const { t: tc } = useTranslation("common");
  const heatTour = useTour("tour_heatmap_done");
  const navigate = useNavigate();
  // Karten-Migration — MapLibre (Basic Dark) behind the provider flag;
  // Leaflet stays as the temporary fallback until removal is approved.
  const useMapLibre = isMapLibreEnabled();
  // Prod-Regression Fix (2026-07-13) — listing-detail deep-link:
  // /heatmap?focus_lat=…&focus_lng=…&focus_ulid=… centres the map on
  // the deal the user came from and drops a highlighted marker.
  const [searchParams, setSearchParams] = useSearchParams();
  // Bug B fix: the memo must depend on the PRIMITIVE param values, not
  // the searchParams object — every viewport/filter URL write created a
  // new `focus` reference which re-triggered the centre effect and
  // snapped the map back to the deal (auto zoom-out bug).
  const focusLatStr = searchParams.get("focus_lat");
  const focusLngStr = searchParams.get("focus_lng");
  const focusUlidStr = searchParams.get("focus_ulid");
  // P0.2 (2026-07-16) — "Deal → Karte → zurück": remember the origin
  // deal so the map can offer a direct way back to it.
  const originUlidStr = searchParams.get("origin_ulid");
  const focus = useMemo(() => {
    const lat = parseFloat(focusLatStr || "");
    const lng = parseFloat(focusLngStr || "");
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return {
      lat,
      lng,
      ulid: focusUlidStr || null,
    };
  }, [focusLatStr, focusLngStr, focusUlidStr]);
  const [bucketType, setBucketType] = useState("auto");
  const [minSamples, setMinSamples] = useState(MIN_SAMPLES_DEFAULT);
  const [buckets, setBuckets] = useState([]);
  const [meta, setMeta] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Stale-Fehlerhinweis: bei transienten Fetch-Fehlern bleiben die
  // letzten Marker sichtbar statt die Karte zu leeren.
  const [exploreError, setExploreError] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  // Welle 5 — Investment Explorer state. Filters live in the URL so
  // back-navigation restores viewport + filters without loss.
  const [filters, setFilters] = useState(() => {
    const f = { ...DEFAULT_FILTERS };
    FILTER_URL_KEYS.forEach((k) => {
      const v = searchParams.get(k);
      if (v) f[k] = v;
    });
    if (f.v !== "auto") f.v = "immo";
    if (LEGACY_MODE_MAP[f.mode]) {
      // legacy deep-links/alerts: alle/ueberteuert → markt (+status)
      Object.assign(f, LEGACY_MODE_MAP[f.mode]);
    }
    if (!VALID_MODES[f.v].includes(f.mode)) {
      f.mode = searchParams.get("src") === "ai" ? "markt" : "deals";
    }
    return f;
  });
  const [view, setView] = useState(
    searchParams.get("view") === "list" ? "list" : "map",
  );
  const [markers, setMarkers] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [clustered, setClustered] = useState(false);
  // null = never loaded → UI shows the loading label, NEVER a "0" flash.
  const [totalCount, setTotalCount] = useState(null);
  const [signalBreakdown, setSignalBreakdown] = useState(null);
  const [totalCapped, setTotalCapped] = useState(false);
  // Welle 5 UX-Fix — never flash "0 results" while the explore fetch is
  // in flight; show an explicit loading label instead.
  const [exploreLoading, setExploreLoading] = useState(false);
  const debounceRef = useRef(null);
  const lastBboxRef = useRef(null);
  const mapRef = useRef(null);
  // Welle 4.3 — fetch hygiene: cancel in-flight requests on pan/zoom,
  // drop stale responses (seq guard), short-lived viewport cache.
  const dealsAbortRef = useRef(null);
  const dealsSeqRef = useRef(0);
  const dealsCacheRef = useRef(new Map());
  // P0-Audit 2026-07-15 — bucket fetch hygiene (previously the bucket
  // request had NO abort/seq guard: stale responses could overwrite
  // newer ones while panning).
  const bucketsAbortRef = useRef(null);
  const bucketsSeqRef = useRef(0);
  const filtersKey = JSON.stringify(filters);

  // V4.12 Phase C — everything derives from vertical + mode (chips).
  const vertical = filters.v === "auto" ? "auto" : "immo";
  const mode = filters.mode;
  const isBucketMode =
    vertical === "immo" && (mode === "preis_m2" || mode === "rendite");
  const showDeals = !isBucketMode;
  const showMarket = isBucketMode;
  const layerId =
    mode === "rendite" ? "yield" : filters.tt === "rent" ? "rent" : "buy";
  const layer = useMemo(() => getLayerById(layerId), [layerId]);

  // dirtyRef — only USER-driven changes sync back to the shared search
  // context (never the initial URL/context hydration).
  const dirtyRef = useRef(false);
  const patchFilters = useCallback((patch) => {
    dirtyRef.current = true;
    setFilters((f) => ({ ...f, ...patch }));
  }, []);
  const setMode = useCallback((m) => {
    dirtyRef.current = true;
    // Master-Sprint P0 — switching layers RESETS the market-status
    // sub-filter: "Markt" must always start at "Alle" (the full market),
    // never inherit a hidden status from a previous state.
    setFilters((f) => ({ ...f, mode: m, status: "" }));
  }, []);
  const [ctxBanner, setCtxBanner] = useState(() =>
    new URLSearchParams(window.location.search).get("src") === "ai"
      ? { label: null }
      : null,
  );
  const setVertical = useCallback((v) => {
    dirtyRef.current = true;
    setCtxBanner(null);
    setFilters((f) => (f.v === v ? f : { ...DEFAULT_FILTERS, v }));
  }, []);
  const clearAppliedContext = useCallback(() => {
    dirtyRef.current = true;
    setCtxBanner(null);
    setFilters((f) => ({ ...DEFAULT_FILTERS, v: f.v }));
  }, []);
  // V4.12 Phase E — save the current search (chips + viewport) as alert.
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  // Master-Sprint P0 — mobile detail filters live in a bottom sheet so
  // the map owns the viewport. The sheet edits a DRAFT and commits it
  // in ONE patch (single refetch).
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const isDesktop = useIsDesktop();
  const activeFilterCount = DETAIL_FILTER_KEYS.filter((k) => filters[k]).length;
  const applyMobileFilters = useCallback(
    (draft) => {
      const patch = {};
      DETAIL_FILTER_KEYS.forEach((k) => { patch[k] = draft[k] || ""; });
      patchFilters(patch);
    },
    [patchFilters],
  );

  // Filter options (master data + central feature taxonomy).
  const [autoOptions, setAutoOptions] = useState(null);
  const [immoOptions, setImmoOptions] = useState(null);
  useEffect(() => {
    if (vertical === "auto" && !autoOptions) {
      apiClient.get("/filter-options/auto").then((r) => setAutoOptions(r.data)).catch(() => {});
    }
    if (vertical === "immo" && !immoOptions) {
      apiClient.get("/filter-options/immo").then((r) => setImmoOptions(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vertical]);
  const [modelsByMake, setModelsByMake] = useState({});
  useEffect(() => {
    csvList(filters.makes).slice(0, 3).forEach((mk) => {
      if (modelsByMake[mk]) return;
      apiClient
        .get("/taxonomy/auto/models", { params: { brand: mk } })
        .then((r) =>
          setModelsByMake((m) => ({
            ...m,
            [mk]: (r.data?.items || []).map((it) => it.model_family),
          })),
        )
        .catch(() => setModelsByMake((m) => ({ ...m, [mk]: [] })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.makes]);
  const modelOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    csvList(filters.makes).forEach((mk) =>
      (modelsByMake[mk] || []).forEach((m) => {
        if (!seen.has(m)) {
          seen.add(m);
          out.push({ value: m, label: m });
        }
      }),
    );
    return out;
  }, [filters.makes, modelsByMake]);

  // AI ↔ Heatmap bridge (read): on plain entry apply the user's last
  // search context (e.g. an AI search). Explicit URL filters win.
  // NOTE: freeze the initial URL state in a ref — the URL-persist
  // effect strips default params before StrictMode re-runs effects.
  const initialUrlRef = useRef({
    srcAi: new URLSearchParams(window.location.search).get("src") === "ai",
    hadFilters: FILTER_URL_KEYS.some((k) =>
      new URLSearchParams(window.location.search).has(k),
    ),
  });
  useEffect(() => {
    if (initialUrlRef.current.srcAi || initialUrlRef.current.hadFilters) {
      return; // caller chose the state explicitly — do not hydrate
    }
    fetchSearchContext()
      .then(({ context, fresh }) => {
        if (!context || !fresh || dirtyRef.current) return;
        const patch = contextToFilterPatch(context);
        if (!patch) return;
        setFilters(patch);
        if (context.source === "ai") setCtxBanner({ label: context.label });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // AI ↔ Heatmap bridge (write): mirror the user's map filters back so
  // the AI advisor knows exactly what they are looking at.
  useEffect(() => {
    if (!dirtyRef.current) return;
    const h = setTimeout(() => {
      putSearchContext({
        vertical,
        filters: filtersToContextPayload(filters, vertical),
      }).catch(() => {});
    }, 1200);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);
  const initial = useMemo(() => {
    // Bug B: a stored viewport (c/z) wins over the focus deep-link —
    // after the user pans, back-navigation restores the LAST viewport
    // instead of snapping back to the deal.
    const c = (searchParams.get("c") || "").split(",").map(Number);
    const z = parseInt(searchParams.get("z") || "", 10);
    if (c.length === 2 && c.every(Number.isFinite) && Number.isFinite(z)) {
      return { center: c, zoom: z };
    }
    if (focus) return { center: [focus.lat, focus.lng], zoom: 13 };
    return EUROPE_VIEW;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  // Sprint A Fix #5 — Stable map-ref callback.
  // The original inline `ref={(m) => { mapRef.current = m; }}` was a
  // fresh closure on every render. React Leaflet's `MapContainer`
  // assigns the ref imperatively; with a fresh function each render
  // React invokes it with (null, then instance) on every commit and
  // — under StrictMode dev double-invocation — can re-trigger
  // internal initialization quirks. A `useCallback` with `[]` deps
  // keeps the callback identity stable for the whole component
  // lifetime, so the map mounts once and stays mounted
  // (HEATMAP_RCA.md CRIT-4).
  const [mapReadyTick, setMapReadyTick] = useState(0);
  const mapRefCallback = useCallback((m) => {
    mapRef.current = m;
    if (m) setMapReadyTick((x) => x + 1);
    // Bug A: re-measure once after commit — if the page mounts while
    // layout is still settling (scroll restore, sticky header) Leaflet's
    // initial size measurement can be wrong (half-rendered map).
    if (m) setTimeout(() => m.invalidateSize(), 0);
  }, []);

  // Welle 5 — ONE param builder: map markers, count and list use the
  // exact same query (identical semantics by construction).
  const exploreParams = useCallback(
    (bbox) => {
      const p = {
        bbox,
        vertical,
        market_position:
          filters.mode === "deals" ? "good_deals" : filters.status || "all",
        // P0 2026-07-19 — Leaflet returns FRACTIONAL zoom on mobile
        // pinch-to-zoom (e.g. 10.5); the backend contract is int 1..19
        // (422 "Aktualisierung fehlgeschlagen" otherwise).
        zoom: Math.max(
          1,
          Math.min(19, Math.round(mapRef.current?.getZoom() ?? EUROPE_VIEW.zoom)),
        ),
      };
      if (filters.price_min) p.price_min = filters.price_min;
      if (filters.price_max) p.price_max = filters.price_max;
      if (filters.region) p.region = filters.region;
      if (filters.country) p.country = filters.country;
      if (filters.features) p.features = filters.features;
      if (filters.seller_types) p.seller_types = filters.seller_types;
      if (filters.price_reduced === "1") p.price_reduced = true;
      if (vertical === "immo") {
        p.transaction_type = filters.tt;
        if (filters.area_min) p.area_min = filters.area_min;
        if (filters.area_max) p.area_max = filters.area_max;
        if (filters.rooms_min) p.rooms_min = filters.rooms_min;
        if (filters.yield_min) p.yield_min = filters.yield_min;
        if (filters.discount_min) p.discount_min = filters.discount_min;
        if (filters.types) p.object_types = filters.types;
      } else {
        if (filters.makes) p.makes = filters.makes;
        if (filters.models) p.models = filters.models;
        if (filters.year_min) p.year_min = filters.year_min;
        if (filters.year_max) p.year_max = filters.year_max;
        if (filters.km_max) p.km_max = filters.km_max;
        if (filters.fuels) p.fuels = filters.fuels;
        if (filters.gearboxes) p.gearboxes = filters.gearboxes;
        if (filters.body_types) p.body_types = filters.body_types;
        if (filters.damage === "1") p.include_damaged = true;
      }
      return p;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtersKey],
  );

  const runExploreFetch = useCallback(
    async (bbox) => {
      if (!bbox) return;
      const params = { ...exploreParams(bbox), mode: "markers", include_breakdown: true };
      const cacheKey = JSON.stringify(params);
      const cached = dealsCacheRef.current.get(cacheKey);
      if (cached && Date.now() - cached.at < DEALS_CACHE_TTL_MS) {
        // V4.10 Phase 7 — a cache hit must ALSO invalidate any in-flight
        // request: without abort + seq bump an older response (different
        // viewport/toggle params) could overwrite the cache-served state
        // seconds later — the "Markt ↔ Deals"-switch state bug.
        if (dealsAbortRef.current) dealsAbortRef.current.abort();
        dealsSeqRef.current += 1;
        setExploreLoading(false);
        setMarkers(cached.markers);
        setClusters(cached.clusters);
        setClustered(cached.clustered);
        setTotalCount(cached.total);
        setTotalCapped(!!cached.capped);
        setSignalBreakdown(cached.breakdown || null);
        setExploreError(false);
        return;
      }
      if (dealsAbortRef.current) dealsAbortRef.current.abort();
      const controller = new AbortController();
      dealsAbortRef.current = controller;
      const seq = ++dealsSeqRef.current;
      setExploreLoading(true);
      try {
        const d = await fetchExploreSearch(params, { signal: controller.signal });
        if (seq !== dealsSeqRef.current) return; // stale viewport response
        const entry = {
          markers: Array.isArray(d?.markers) ? d.markers : [],
          clusters: Array.isArray(d?.clusters) ? d.clusters : [],
          clustered: !!d?.clustered,
          total: d?.total_count ?? 0,
          capped: !!d?.total_count_capped,
          breakdown: d?.signal_breakdown || null,
          at: Date.now(),
        };
        dealsCacheRef.current.set(cacheKey, entry);
        if (dealsCacheRef.current.size > 40) {
          dealsCacheRef.current.delete(
            dealsCacheRef.current.keys().next().value,
          );
        }
        setMarkers(entry.markers);
        setClusters(entry.clusters);
        setClustered(entry.clustered);
        setTotalCount(entry.total);
        setTotalCapped(entry.capped);
        setSignalBreakdown(entry.breakdown);
        setExploreError(false);
      } catch (err) {
        if (seq !== dealsSeqRef.current) return;
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        // P0-Audit 2026-07-15 — transient failure: KEEP the last
        // markers on screen (previously setMarkers([]) blanked the map
        // whenever one viewport request 500ed/timed out) and surface a
        // subtle stale-hint instead. The next pan retries naturally.
        setExploreError(true);
      } finally {
        // Root cause of the endless spinner: setExploreLoading(false)
        // did not exist anywhere before this audit.
        if (seq === dealsSeqRef.current) setExploreLoading(false);
      }
    },
    [exploreParams],
  );

  const runFetch = useCallback(
    async (bbox) => {
      if (!layer) return;
      lastBboxRef.current = bbox; // V4.8 — remembered for manual retry
      // Explorer markers need a bbox (viewport); fetched alongside buckets.
      if (showDeals && bbox) {
        runExploreFetch(bbox);
      } else if (!showDeals) {
        // V4.10 Phase 7 — toggle-off must abort + invalidate the
        // in-flight deals request: previously a late response
        // resurrected the markers (and a late failure pinned the
        // "Aktualisierung fehlgeschlagen"-hint) although the layer
        // was already off.
        if (dealsAbortRef.current) dealsAbortRef.current.abort();
        dealsSeqRef.current += 1;
        setExploreLoading(false);
        setExploreError(false);
        setMarkers([]);
        setClusters([]);
        setTotalCount(0);
        setTotalCapped(false);
      }
      if (!showMarket) {
        if (bucketsAbortRef.current) bucketsAbortRef.current.abort();
        bucketsSeqRef.current += 1;
        setBuckets([]);
        setMeta(null);
        setError(null);
        setIsLoading(false);
        return;
      }
      if (bucketsAbortRef.current) bucketsAbortRef.current.abort();
      const controller = new AbortController();
      bucketsAbortRef.current = controller;
      const seq = ++bucketsSeqRef.current;
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchHeatmapBuckets(layer.endpoint, {
          bucket_type: bucketType,
          bbox,
          min_samples: minSamples,
          limit: 2000,
        }, { signal: controller.signal });
        if (seq !== bucketsSeqRef.current) return; // stale response
        let items = Array.isArray(data?.buckets) ? data.buckets : [];
        // Performance cap — if the response is huge, suggest a coarser
        // bucket-type. We display the top-N by sample count so the map
        // still renders something useful.
        let truncated = false;
        if (items.length > PERFORMANCE_BUCKET_CAP) {
          truncated = true;
          items = [...items]
            .sort((a, b) => (b.n_samples || 0) - (a.n_samples || 0))
            .slice(0, PERFORMANCE_BUCKET_CAP);
        }
        setBuckets(items);
        setMeta({ ...data, truncated, returned: items.length });
      } catch (err) {
        if (seq !== bucketsSeqRef.current) return;
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          t("unknownError");
        setError(msg);
        toast.error(t("loadError"), {
          description: String(msg),
        });
        // Keep the last buckets on screen — stale beats blank.
      } finally {
        if (seq === bucketsSeqRef.current) setIsLoading(false);
      }
    },
    [layer, bucketType, minSamples, showDeals, showMarket, runExploreFetch, t],
  );

  // Debounced fetch on pan/zoom — coalesce rapid moveend events.
  const onBoundsChange = useCallback(
    (bounds) => {
      const bbox = bboxFromLeafletBounds(bounds);
      lastBboxRef.current = bbox;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        runFetch(bbox);
        // Welle 5 — persist viewport in the URL for lossless back-nav.
        const m = mapRef.current;
        if (m) {
          const c = m.getCenter();
          const next = new URLSearchParams(window.location.search);
          next.set("c", `${c.lat.toFixed(5)},${c.lng.toFixed(5)}`);
          next.set("z", String(m.getZoom()));
          setSearchParams(next, { replace: true });
        }
      }, MOVE_DEBOUNCE_MS);
    },
    [runFetch, setSearchParams],
  );

  // Welle 5 — persist filters + view in the URL (defaults stay clean).
  useEffect(() => {
    const next = new URLSearchParams(window.location.search);
    FILTER_URL_KEYS.forEach((k) => {
      if (filters[k] && filters[k] !== DEFAULT_FILTERS[k]) next.set(k, filters[k]);
      else next.delete(k);
    });
    if (view === "list") next.set("view", "list");
    else next.delete("view");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, view]);

  // Re-fetch whenever layer toggles / bucket type / min-samples /
  // explorer filters change.
  useEffect(() => {
    if (lastBboxRef.current) {
      runFetch(lastBboxRef.current);
    } else {
      runFetch(null); // first paint — no bbox yet
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerId, bucketType, minSamples, showDeals, showMarket, filtersKey]);

  // HARD INVARIANT (Bug B): automatic centring happens exactly ONCE on
  // the initial deep-link entry. Afterwards user interaction always
  // wins — marker/count/list responses and URL writes must NEVER move
  // the viewport. If a viewport (c/z) is already in the URL, the
  // restore path owns the view and focus centring is skipped entirely.
  const focusAppliedRef = useRef(
    Boolean(new URLSearchParams(window.location.search).get("c")),
  );
  useEffect(() => {
    if (!mapRef.current || !focus || focusAppliedRef.current) return;
    focusAppliedRef.current = true;
    mapRef.current.setView([focus.lat, focus.lng], 13);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  // V4.23 Phase 3a+ — Auto-Fly-to: exactly ONCE per NEW place selection.
  // Multiple hits → fitBounds on their bbox; single hit → detail zoom;
  // no hits / no coords → no map movement. Other filter changes never
  // re-centre and manual panning always wins afterwards. A viewport
  // (c/z) in the URL owns the initial view (deep-link restore).
  const flownRegionRef = useRef(
    new URLSearchParams(window.location.search).get("c")
      ? (filters.region || "").trim()
      : "",
  );
  useEffect(() => {
    const region = (filters.region || "").trim();
    if (!mapRef.current || region === flownRegionRef.current) {
      if (!region) flownRegionRef.current = "";
      return;
    }
    flownRegionRef.current = region;
    let cancelled = false;
    (async () => {
      try {
        const { locatePlace } = await import("../api/taxonomyApi");
        const res = await locatePlace(region, filters.country || null, vertical);
        const m = mapRef.current;
        if (cancelled || !m || !res?.found || !res.count || !res.bbox) return;
        const [minLat, minLng, maxLat, maxLng] = res.bbox;
        const spread = Math.max(
          Math.abs(maxLat - minLat), Math.abs(maxLng - minLng));
        if (res.count === 1 || spread < 0.005) {
          m.setView(res.center, 14);
        } else {
          m.fitBounds([[minLat, minLng], [maxLat, maxLng]], {
            padding: [48, 48],
          });
        }
      } catch { /* fly-to is best-effort — never break the map */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.region, mapReadyTick]);

  // Domain (min/max) for the colour scale — recomputed per fetch.
  const domain = useMemo(() => {
    if (!buckets.length) return [0, 1];
    const vals = buckets
      .map((b) => b?.avg)
      .filter((v) => typeof v === "number" && Number.isFinite(v));
    if (!vals.length) return [0, 1];
    return [Math.min(...vals), Math.max(...vals)];
  }, [buckets]);

  return (
    <div data-testid="heatmap-page" className="space-y-3">
      <header className="space-y-1">
        {originUlidStr && (
          <button
            type="button"
            data-testid="heatmap-back-to-deal"
            onClick={() => navigate(`/listing/${originUlidStr}`)}
            className="inline-flex items-center gap-2 rounded-full border border-emerald-500/50 bg-emerald-500/10 px-4 py-1.5 text-xs font-medium text-emerald-300 transition-colors hover:bg-emerald-500/20"
          >
            ← {t("backToDeal", { defaultValue: "Zurück zum Deal" })}
          </button>
        )}
        <div className="flex items-center gap-3">
          <h1 className="text-lg md:text-2xl text-zinc-100 font-medium tracking-tight">
            {t("title")}
          </h1>
          {/* Kompakter Europa-Chip im Header — Geografie bekommt keine
              eigene fast leere Zeile, die Karte dominiert. */}
          <span
            data-testid="heatmap-europe-indicator"
            title={t("europeHint")}
            className="ml-auto hidden md:inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-zinc-900 px-2.5 py-1 text-[11px] text-zinc-400"
          >
            <Globe className="h-3 w-3 text-cyan-400" aria-hidden />
            {t("europe")}
          </span>
        </div>
      </header>

      {/* V4.12 Phase C — Premium chip bar: vertical + context modes.
          Master-Sprint P0 — mobile gets compact controls + a bottom
          sheet instead of the full chip wall. */}
      <div
        data-testid="heatmap-controls"
        className="space-y-2 rounded-xl border border-zinc-800 bg-zinc-900/40 p-2 md:p-3"
      >
        {!isDesktop && (
          <HeatmapMobileControls
            vertical={vertical}
            mode={mode}
            filters={filters}
            onVertical={setVertical}
            onMode={setMode}
            onPatch={patchFilters}
            onOpenFilters={() => setMobileFiltersOpen(true)}
            activeFilterCount={activeFilterCount}
            ctxBanner={ctxBanner}
            onClearContext={clearAppliedContext}
            t={t}
          />
        )}
        {isDesktop && (
          <HeatmapChipBar
            vertical={vertical}
            mode={mode}
            filters={filters}
            onVertical={setVertical}
            onMode={setMode}
            onPatch={patchFilters}
            autoOptions={autoOptions}
            immoOptions={immoOptions}
            modelOptions={modelOptions}
            t={t}
          />
        )}

        {(ctxBanner || isLoading || (showMarket && meta)) && (
        <div className="hidden md:flex flex-wrap items-center gap-3">
          {ctxBanner && (
            <span
              data-testid="heatmap-ai-context-chip"
              className="inline-flex items-center gap-1.5 rounded-full border border-sky-500/40 bg-sky-500/10 px-3 py-1.5 text-[11px] font-medium text-sky-300"
            >
              <Sparkles className="h-3 w-3" />
              {t("chips.aiBanner")}
              {ctxBanner.label ? `: ${ctxBanner.label}` : ""}
              <button
                type="button"
                data-testid="heatmap-ai-context-clear"
                onClick={clearAppliedContext}
                title={t("chips.clearAll")}
                className="rounded-full px-1 hover:bg-sky-500/20"
              >
                ×
              </button>
            </span>
          )}
          {/* Status */}
          <div className="flex items-center gap-3 text-xs text-zinc-400">
            {isLoading && (
              <span
                data-testid="heatmap-loading"
                className="inline-flex items-center gap-1.5 text-amber-300"
              >
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t("loading")}
              </span>
            )}
            {!isLoading && showMarket && meta && (
              <span data-testid="heatmap-count">
                {t("buckets", { count: meta.returned ?? meta.total_buckets })}
                {meta.truncated ? t("truncated") : ""}
              </span>
            )}
          </div>
        </div>
        )}

        {/* Weitere Optionen (Raster, Min Samples) — Markt-Layer only */}
        {showMarket && (
        <div className="hidden md:block">
          <button
            type="button"
            data-testid="heatmap-more-options"
            onClick={() => setMoreOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-widest text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            <span aria-hidden="true">{moreOpen ? "▾" : "▸"}</span>
            {t("ux.moreOptions")}
          </button>
          {moreOpen && (
            <div className="mt-2 flex flex-wrap items-center gap-4">
              {showMarket && (
                <label className="text-xs text-zinc-400 flex items-center gap-2">
                  {t("ux.grid")}
                  <span className="min-w-[150px]">
                    <DarkSelect
                      testid="heatmap-bucket-type"
                      compact
                      value={bucketType}
                      onChange={(val) => setBucketType(val)}
                      options={BUCKET_TYPES.map((b) => ({
                        value: b,
                        label: t(`bucketType.${b}`, { defaultValue: b }),
                      }))}
                    />
                  </span>
                </label>
              )}
              {showMarket && (
                <label className="text-xs text-zinc-400 flex items-center gap-2">
                  {t("minSamples")}
                  <input
                    type="number"
                    data-testid="heatmap-min-samples"
                    value={minSamples}
                    min={1}
                    max={500}
                    onChange={(e) =>
                      setMinSamples(Math.max(1, Number(e.target.value) || 1))
                    }
                    className="w-16 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-zinc-100"
                  />
                </label>
              )}
            </div>
          )}
        </div>
        )}
      </div>

      {/* Welle 5 — viewport match count + active filter chips + list */}
      {showDeals && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-500/20 bg-zinc-900/60 px-4 py-2.5">
          <span
            data-testid="explore-match-count"
            className="text-sm font-medium text-zinc-100 inline-flex items-center gap-2"
          >
            {totalCount == null
              ? t("explore.countLoading")
              : t(
                  vertical === "auto"
                    ? "explore.matchCountAuto"
                    : "explore.matchCount",
                  { count: totalCount },
                ) + (totalCapped ? "+" : "")}
            {exploreLoading && (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-400" />
            )}
            {exploreError && !exploreLoading && (
              <span
                data-testid="explore-stale-hint"
                className="text-[11px] text-amber-400/90"
              >
                {t("explore.staleHint")}
              </span>
            )}
          </span>
          <button
            type="button"
            data-testid="save-alert-button"
            onClick={() => setAlertDialogOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-200 transition-colors hover:border-amber-500/50 hover:text-amber-300"
          >
            <Bell className="h-3.5 w-3.5" />
            {tc("alerts.saveButton")}
          </button>
          <button
            type="button"
            data-testid="explore-open-list"
            onClick={() => setView("list")}
            className="ml-auto rounded-lg bg-amber-500 px-4 py-1.5 text-xs font-semibold text-zinc-950 transition-colors hover:bg-amber-400"
          >
            {t("explore.openList")}
          </button>
          {/* V4.23 — the SAME central hit-count structure as AI/Alerts */}
          {signalBreakdown?.total > 0 && (
            <div
              data-testid="explore-signal-breakdown"
              className="flex w-full flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-zinc-500"
            >
              {signalBreakdown.under_market > 0 && (
                <span>
                  <span className="font-semibold text-emerald-300">
                    {signalBreakdown.under_market}
                  </span>{" "}
                  {t("explore.breakdownUnder")}
                </span>
              )}
              {signalBreakdown.fair > 0 && (
                <span>
                  <span className="font-semibold text-zinc-300">
                    {signalBreakdown.fair}
                  </span>{" "}
                  {t("explore.breakdownFair")}
                </span>
              )}
              {signalBreakdown.over_market > 0 && (
                <span>
                  <span className="font-semibold text-rose-300">
                    {signalBreakdown.over_market}
                  </span>{" "}
                  {t("explore.breakdownOver")}
                </span>
              )}
              {signalBreakdown.no_signal > 0 && (
                <span>
                  <span className="font-semibold text-zinc-500">
                    {signalBreakdown.no_signal}
                  </span>{" "}
                  {t("explore.breakdownNoSignal")}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Map */}
      <div
        data-testid="heatmap-map-wrap"
        className="relative h-[60vh] min-h-[420px] overflow-hidden rounded-2xl border border-zinc-800"
      >
        {useMapLibre ? (
          <HeatmapMapLibre
            initial={initial}
            onMapRef={mapRefCallback}
            onBoundsChange={onBoundsChange}
            buckets={buckets}
            domain={domain}
            layer={layer}
            markers={markers}
            clusters={clusters}
            clustered={clustered}
            showMarket={showMarket}
            showDeals={showDeals}
            focus={focus}
            onOpenListing={(ulid) => navigate(`/listing/${ulid}`)}
            t={t}
          />
        ) : (
        <MapContainer
          center={initial.center}
          zoom={initial.zoom}
          scrollWheelZoom
          style={{ height: "100%", width: "100%", background: "#09090b" }}
          ref={mapRefCallback}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <BboxListener onChange={onBoundsChange} />
          {/* Welle 4.2/5 — clickable deal markers: the map opens deals. */}
          {showDeals && !clustered &&
            markers.map((d) => {
              if (!Number.isFinite(d.lat) || !Number.isFinite(d.lng)) return null;
              return (
                <Marker
                  key={`deal-${d.ulid}`}
                  position={[d.lat, d.lng]}
                  icon={dealIcon(d)}
                  zIndexOffset={1000}
                >
                  <Popup className="heatmap-deal-popup" maxWidth={240}>
                    <div
                      data-testid={`heatmap-deal-popup-${d.ulid}`}
                      className="w-[230px] overflow-hidden text-zinc-100"
                    >
                      {d.primary_image_url && (
                        <img
                          src={d.primary_image_url}
                          alt=""
                          loading="lazy"
                          className="h-28 w-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      )}
                      <div className="space-y-1.5 p-3">
                        <div className="line-clamp-2 text-[13px] font-medium leading-snug text-zinc-100">
                          {d.title}
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-zinc-100">
                            {typeof d.price === "number"
                              ? `€ ${Math.round(d.price).toLocaleString("de-AT")}`
                              : "—"}
                          </span>
                          {typeof d.price_vs_market_pct === "number" && (
                            <span
                              className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                                d.price_vs_market_pct > 0
                                  ? "border-orange-500/50 bg-orange-500/15 text-orange-300"
                                  : "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"
                              }`}
                            >
                              {d.price_vs_market_pct > 0 ? "+" : "−"}
                              {Math.abs(Math.round(d.price_vs_market_pct))} %
                            </span>
                          )}
                        </div>
                        {d.low_confidence && (
                          <div
                            data-testid={`heatmap-deal-lowconf-${d.ulid}`}
                            className="text-[10px] font-medium text-amber-400/90"
                          >
                            {t("lowConfidence")}
                          </div>
                        )}
                        <button
                          type="button"
                          data-testid={`heatmap-deal-open-${d.ulid}`}
                          onClick={() => navigate(`/listing/${d.ulid}`)}
                          className="mt-1 w-full rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-zinc-950 transition-colors hover:bg-amber-400"
                        >
                          {t("viewDeal")}
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          {/* Welle 5 — zoom-dependent cluster bubbles (click → zoom in) */}
          {showDeals && clustered &&
            clusters.map((c, i) => (
              <Marker
                key={`cluster-${i}-${c.count}`}
                position={[c.lat, c.lng]}
                icon={clusterIcon(c.count)}
                zIndexOffset={900}
                eventHandlers={{
                  click: () => {
                    const m = mapRef.current;
                    if (!m) return;
                    const [aLat, aLng, bLat, bLng] = c.bounds || [];
                    if (
                      Number.isFinite(aLat) &&
                      Number.isFinite(bLat) &&
                      (Math.abs(bLat - aLat) > 0.0005 ||
                        Math.abs(bLng - aLng) > 0.0005)
                    ) {
                      m.fitBounds(
                        [[aLat, aLng], [bLat, bLng]],
                        { padding: [48, 48] },
                      );
                    } else {
                      m.setView(
                        [c.lat, c.lng],
                        Math.min(18, (m.getZoom() ?? 10) + 2),
                      );
                    }
                  },
                }}
              />
            ))}
          {focus && (
            <CircleMarker
              center={[focus.lat, focus.lng]}
              radius={11}
              pathOptions={{
                className: "heatmap-focus-marker",
                color: "#f59e0b",
                weight: 3,
                fillColor: "#f59e0b",
                fillOpacity: 0.35,
              }}
            >
              <Popup>
                <div className="min-w-[140px] text-zinc-100">
                  <div className="font-semibold text-amber-300">{t("yourDeal")}</div>
                  <div className="text-xs text-zinc-400">
                    {t("approxLocation")}
                  </div>
                </div>
              </Popup>
            </CircleMarker>
          )}
          {buckets.map((b) => {
            const lat = b.latitude;
            const lng = b.longitude;
            if (
              typeof lat !== "number" ||
              typeof lng !== "number" ||
              !Number.isFinite(lat) ||
              !Number.isFinite(lng)
            ) {
              return null;
            }
            const fill = colourFor(b.avg, domain[0], domain[1], layer.colorScale);
            return (
              <CircleMarker
                key={`${b.bucket_type}:${b.bucket_key}`}
                center={[lat, lng]}
                radius={Math.max(7, Math.min(20, Math.sqrt(b.n_samples) * 2))}
                pathOptions={{
                  color: "#18181b",
                  weight: 1,
                  fillColor: fill,
                  fillOpacity: 0.78,
                }}
              >
                <Popup>
                  <div
                    data-testid={`heatmap-popup-${b.bucket_key}`}
                    className="min-w-[180px] space-y-1 text-zinc-100"
                  >
                    <div className="font-semibold text-amber-300">
                      {t(`layers.${layer.id}.label`, { defaultValue: layer.label })}
                    </div>
                    <div className="text-xs text-zinc-400">
                      {b.bucket_type} · {b.bucket_key}
                    </div>
                    <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                      <span className="text-zinc-400">avg</span>
                      <span className="text-zinc-100 font-medium">
                        {formatValue(b.avg, layer.format)}
                      </span>
                      {b.median !== undefined && (
                        <>
                          <span className="text-zinc-400">median</span>
                          <span className="text-zinc-100">
                            {formatValue(b.median, layer.format)}
                          </span>
                        </>
                      )}
                      {b.p25 !== undefined && (
                        <>
                          <span className="text-zinc-400">p25</span>
                          <span className="text-zinc-100">
                            {formatValue(b.p25, layer.format)}
                          </span>
                        </>
                      )}
                      {b.p75 !== undefined && (
                        <>
                          <span className="text-zinc-400">p75</span>
                          <span className="text-zinc-100">
                            {formatValue(b.p75, layer.format)}
                          </span>
                        </>
                      )}
                      <span className="text-zinc-400">samples</span>
                      <span className="text-zinc-100">{b.n_samples}</span>
                    </div>
                    {/* V4.2 — hand the region over to the AI market chat */}
                    <div className="mt-2 flex flex-col gap-1">
                      <button
                        type="button"
                        data-testid={`heatmap-ask-ai-${b.bucket_key}`}
                        onClick={() =>
                          navigate(
                            `/ai?ask=${encodeURIComponent(
                              t("askAiRegion", {
                                region: b.bucket_key,
                                defaultValue: `Analysiere die Region ${b.bucket_key}`,
                              }),
                            )}`,
                          )
                        }
                        className="w-full rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-300 transition-colors hover:bg-amber-500/20"
                      >
                        🤖 {t("askAiRegionLabel", { defaultValue: "Region mit KI analysieren" })}
                      </button>
                      <button
                        type="button"
                        data-testid={`heatmap-ask-ai-deals-${b.bucket_key}`}
                        onClick={() =>
                          navigate(
                            `/ai?ask=${encodeURIComponent(
                              t("askAiDeals", {
                                region: b.bucket_key,
                                defaultValue: `Zeig mir gute Deals in der Region ${b.bucket_key}`,
                              }),
                            )}`,
                          )
                        }
                        className="w-full rounded-lg border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 transition-colors hover:bg-zinc-800"
                      >
                        {t("askAiDealsLabel", { defaultValue: "Passende Deals zeigen" })}
                      </button>
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
        )}

        {/* Empty / error overlays */}
        {!isLoading &&
          !exploreLoading &&
          !error &&
          (showMarket || showDeals) &&
          (!showMarket || buckets.length === 0) &&
          !(showDeals && (markers.length > 0 || clusters.length > 0)) && (
          <div
            data-testid="heatmap-empty"
            className="pointer-events-none absolute inset-0 flex items-center justify-center bg-zinc-950/50"
          >
            <div className="rounded-xl border border-zinc-700 bg-zinc-900/90 px-5 py-3 text-sm text-zinc-300">
              {t("empty")}
            </div>
          </div>
        )}
        {error && (
          <div
            data-testid="heatmap-error"
            className="absolute inset-x-4 bottom-4 flex items-center justify-between gap-3 rounded-xl border border-rose-500/40 bg-rose-950/80 px-4 py-2 text-xs text-rose-200"
          >
            <span className="truncate">{String(error)}</span>
            <button
              type="button"
              data-testid="heatmap-retry"
              onClick={() => runFetch(lastBboxRef.current)}
              className="shrink-0 rounded-lg border border-rose-400/40 px-3 py-1 font-medium text-rose-100 transition-colors hover:bg-rose-500/20"
            >
              {t("retry", { defaultValue: "Erneut versuchen" })}
            </button>
          </div>
        )}
      </div>

      {/* Legend — market layer only */}
      {showMarket && (
      <div
        data-testid="heatmap-legend"
        className="flex flex-wrap items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3 text-xs text-zinc-400"
      >
        <span className="text-zinc-300 font-medium">{t("scale")}</span>
        <span>{formatValue(domain[0], layer?.format)}</span>
        <div
          aria-hidden="true"
          className="h-2 w-48 rounded"
          style={{
            background: `linear-gradient(to right, ${colourFor(
              domain[0],
              domain[0],
              domain[1],
              layer?.colorScale,
            )}, ${colourFor(
              (domain[0] + domain[1]) / 2,
              domain[0],
              domain[1],
              layer?.colorScale,
            )}, ${colourFor(domain[1], domain[0], domain[1], layer?.colorScale)})`,
          }}
        />
        <span>{formatValue(domain[1], layer?.format)}</span>
        <span className="ml-4 text-zinc-500">
          {t("legendHint")}
        </span>
      </div>
      )}

      {/* Deal pin colour legend — shared contract (identical on mobile) */}
      {showDeals && (markers.length > 0 || clusters.length > 0) && (
        <div
          data-testid="heatmap-deal-legend"
          className="flex flex-wrap items-center gap-4 rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-2.5 text-xs text-zinc-400"
        >
          {[
            ["#22c55e", t("ux.legendStrong")],
            ["#fbbf24", t("ux.legendNeutral")],
            ["#e4e4e7", t("ux.legendNormal")],
            ["#ef4444", t("ux.legendOver")],
          ].map(([c, label]) => (
            <span key={label} className="inline-flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="h-2.5 w-2.5 rounded-full border border-black/50"
                style={{ backgroundColor: c }}
              />
              {label}
            </span>
          ))}
        </div>
      )}

      {/* TourGuide — heatmap spotlight tour (once per user) */}
      {heatTour.eligible && (
        <SpotlightTour
          idPrefix="tour-heatmap"
          onFinish={heatTour.finish}
          steps={[
            {
              target: '[data-testid="heatmap-mode-chips"]',
              title: tc("tour.heatmap.step1_title"),
              body: tc("tour.heatmap.step1_body"),
            },
            {
              target: '[data-testid="heatmap-subchips"]',
              title: tc("tour.heatmap.step2_title"),
              body: tc("tour.heatmap.step2_body"),
            },
            {
              target: '[data-testid="heatmap-map-wrap"]',
              title: tc("tour.heatmap.step3_title"),
              body: tc("tour.heatmap.step3_body"),
            },
          ]}
        />
      )}

      {/* Welle 5 — Investment Explorer list panel */}
      <ExploreListPanel
        open={view === "list"}
        onClose={() => setView("map")}
        initialTotal={totalCount}
        vertical={vertical}
        baseParams={
          lastBboxRef.current ? exploreParams(lastBboxRef.current) : {}
        }
        t={t}
      />
      {/* Master-Sprint P0 — mobile detail-filter bottom sheet */}
      {!isDesktop && (
        <HeatmapMobileFilterSheet
          open={mobileFiltersOpen}
          onClose={() => setMobileFiltersOpen(false)}
          vertical={vertical}
          mode={mode}
          filters={filters}
          onApply={applyMobileFilters}
          autoOptions={autoOptions}
          immoOptions={immoOptions}
          modelOptions={modelOptions}
          t={t}
        />
      )}
      {/* V4.12 Phase E — save current search as alert */}
      <SaveAlertDialog
        open={alertDialogOpen}
        onOpenChange={setAlertDialogOpen}
        vertical={vertical}
        filters={filtersToContextPayload(filters, vertical)}
        bbox={
          lastBboxRef.current
            ? lastBboxRef.current.split(",").map(Number)
            : null
        }
        label={ctxBanner?.label || null}
        source="heatmap"
      />
    </div>
  );
}
